import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { apiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DatePickerFR } from "@/components/ui/date-picker-fr";
import SupplierCombobox from "@/components/SupplierCombobox";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Upload, FileText, AlertTriangle, AlertCircle, CheckCircle2, Loader2, ArrowLeft, Save,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

/**
 * Two-step supplier-invoice creation:
 *   1. Upload PDF/image → Gemini extraction
 *   2. Verification screen with pre-filled but editable fields + issues visible
 *
 * The two steps are DISTINCT (not a single-page auto-flow) so the user is
 * forced to pause and re-read every extracted value before saving — matching
 * the non-negotiable arithmetic-validation posture.
 */
export default function SupplierInvoiceUpload() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);      // 1: upload | 2: review
  const [uploading, setUploading] = useState(false);
  const [pcg, setPcg] = useState([]);
  const [state, setState] = useState({
    fileAssetId: null,
    validation: null,
    fileName: null,
    filePreviewUrl: null,
    extracted: null,             // form values (may start from Gemini)
    saving: false,
  });

  useEffect(() => {
    api.get("/purchases/pcg-categories").then((r) => setPcg(r.data.categories || []));
  }, []);

  const onFilePicked = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (10 Mo max).");
      return;
    }
    setUploading(true);
    setState((s) => ({ ...s, fileName: file.name,
                        filePreviewUrl: URL.createObjectURL(file) }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/supplier-invoices/extract", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const ext = r.data.extracted || {};
      const today = new Date().toISOString().slice(0, 10);
      setState((s) => ({
        ...s,
        fileAssetId: r.data.file_asset_id,
        validation: r.data.validation,
        duplicates: r.data.duplicates || { hard: [], soft: [] },
        extracted: {
          supplier_name: ext.supplier_name || "",
          supplier_siren: ext.supplier_siren || "",
          supplier_iban: ext.supplier_iban || "",
          supplier_vat_number: ext.supplier_vat_number || "",
          invoice_number: ext.invoice_number || "",
          invoice_date: ext.invoice_date || today,
          due_date: ext.due_date || "",
          currency: ext.currency || "EUR",
          total_ht: ext.total_ht ?? 0,
          total_vat: ext.total_vat ?? 0,
          total_ttc: ext.total_ttc ?? 0,
          pcg_code: ext.pcg_code || "606800",
          lines: ext.lines || [],
        },
      }));
      setStep(2);
      if (!ext || !ext.supplier_name) {
        toast.warning(t("purchases.upload.extractError"));
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setUploading(false);
    }
  };

  const save = async (opts = {}) => {
    const p = state.extracted;
    if (!p.supplier_name || !p.invoice_number || !p.invoice_date) {
      toast.error("Fournisseur, numéro et date sont obligatoires.");
      return;
    }
    setState((s) => ({ ...s, saving: true }));
    try {
      const body = { ...p, file_asset_id: state.fileAssetId, source: "ocr_gemini" };
      // Ensure numerics.
      body.total_ht = Number(body.total_ht);
      body.total_vat = Number(body.total_vat);
      body.total_ttc = Number(body.total_ttc);
      body.lines = (p.lines || []).map((ln) => ({
        description: ln.description || "",
        quantity: Number(ln.quantity || 1),
        unit_price_ht: Number(ln.unit_price_ht || 0),
        vat_rate: Number(ln.vat_rate || 20),
        total_ht: Number(ln.total_ht || 0),
      }));
      let url = "/supplier-invoices";
      if (opts.force) {
        const reason = encodeURIComponent(opts.force_reason || "");
        url = `/supplier-invoices?force=true&force_reason=${reason}`;
      }
      const r = await api.post(url, body);
      toast.success("Facture fournisseur enregistrée.");
      navigate(`/app/purchases/supplier-invoices/${r.data.invoice.id}`);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409 && detail?.error === "duplicate_invoice") {
        // Surface the duplicates: user must acknowledge before force-saving.
        setState((s) => ({
          ...s,
          saving: false,
          duplicates: { hard: detail.duplicates || [], soft: (s.duplicates || {}).soft || [] },
          duplicateBlockMessage: detail.message,
        }));
        toast.error(detail.message);
        return;
      }
      toast.error(apiError(e));
      setState((s) => ({ ...s, saving: false }));
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="supplier-invoice-upload-page">
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <button className="hover:text-slate-900 inline-flex items-center gap-1"
                onClick={() => navigate("/app/purchases")}
                data-testid="upload-back-btn">
          <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2} />
          {t("purchases.upload.back")}
        </button>
      </div>

      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900"
            style={{ fontFamily: "Manrope, sans-serif" }}>
          {t("purchases.upload.title")}
        </h1>
      </div>

      <StepIndicator current={step} />

      {step === 1 && (
        <Step1Upload uploading={uploading} onFilePicked={onFilePicked} t={t} />
      )}

      {step === 2 && state.extracted && (
        <Step2Review
          state={state} setState={setState} pcg={pcg}
          onCancel={() => setStep(1)}
          onSave={save}
          t={t}
        />
      )}
    </div>
  );
}


function StepIndicator({ current }) {
  return (
    <div className="flex items-center gap-3">
      {[1, 2].map((n) => (
        <div key={n} className="flex items-center gap-2">
          <div className={cn(
            "h-6 w-6 rounded-full flex items-center justify-center text-[11px] font-semibold",
            current >= n ? "bg-blue-600 text-white" : "bg-slate-200 text-slate-500"
          )}
          data-testid={`step-indicator-${n}`}>
            {n}
          </div>
          {n < 2 && (
            <div className={cn("h-px w-16",
              current > n ? "bg-blue-600" : "bg-slate-200")} />
          )}
        </div>
      ))}
    </div>
  );
}


function Step1Upload({ uploading, onFilePicked, t }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const handleDrop = useCallback((e) => {
    e.preventDefault(); setDrag(false);
    const f = e.dataTransfer.files?.[0];
    if (f) onFilePicked(f);
  }, [onFilePicked]);

  return (
    <Card className="p-8 border-slate-200 rounded-xl bg-white" data-testid="upload-step1">
      <div className="text-lg font-medium text-slate-800">{t("purchases.upload.step1Title")}</div>
      <p className="mt-1 text-sm text-slate-500">{t("purchases.upload.step1Sub")}</p>

      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={handleDrop}
        className={cn(
          "mt-6 border-2 border-dashed rounded-xl px-8 py-14 flex flex-col items-center justify-center text-center cursor-pointer transition-colors",
          drag ? "border-blue-500 bg-blue-50" : "border-slate-300 hover:border-slate-400 hover:bg-slate-50",
          uploading && "pointer-events-none opacity-70"
        )}
        data-testid="upload-dropzone"
      >
        {uploading ? (
          <>
            <Loader2 className="h-10 w-10 text-blue-600 animate-spin" strokeWidth={1.5} />
            <div className="mt-3 text-sm text-slate-700">{t("purchases.upload.extracting")}</div>
          </>
        ) : (
          <>
            <Upload className="h-10 w-10 text-slate-400" strokeWidth={1.5} />
            <div className="mt-3 text-sm text-slate-700">{t("purchases.upload.dropHere")}</div>
            <div className="mt-1 text-xs text-slate-400">{t("purchases.upload.allowedTypes")}</div>
          </>
        )}
        <input
          id="upload-file-input"
          name="upload-file-input"
          ref={inputRef} type="file" className="hidden"
          accept="application/pdf,image/jpeg,image/png,image/webp"
          onChange={(e) => onFilePicked(e.target.files?.[0])}
          data-testid="upload-file-input"
        />
      </div>
    </Card>
  );
}


function Step2Review({ state, setState, pcg, onCancel, onSave, t }) {
  const p = state.extracted;
  const v = state.validation;
  const dups = state.duplicates || { hard: [], soft: [] };
  const hasHardDup = (dups.hard || []).length > 0;
  const hasSoftDup = (dups.soft || []).length > 0;
  const [forceDialogOpen, setForceDialogOpen] = useState(false);
  const [forceReason, setForceReason] = useState("");
  const setField = (name, value) =>
    setState((s) => ({ ...s, extracted: { ...s.extracted, [name]: value } }));

  const confirmForce = () => {
    if (forceReason.trim().length < 5) return;
    setForceDialogOpen(false);
    onSave({ force: true, force_reason: forceReason.trim() });
  };

  const errors = (v?.issues || []).filter((i) =>
    (i.severity ?? (["line_total_mismatch", "total_ht_mismatch", "total_ttc_mismatch"]
      .includes(i.code) ? "error" : "warning")) === "error");
  const warns = (v?.issues || []).filter((i) => !errors.includes(i));

  return (
    <Card className="p-8 border-slate-200 rounded-xl bg-white space-y-6" data-testid="upload-step2">
      <div>
        <div className="text-lg font-medium text-slate-800">{t("purchases.upload.step2Title")}</div>
        <p className="mt-1 text-sm text-slate-500">{t("purchases.upload.step2Sub")}</p>
        {state.fileName && (
          <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-slate-500">
            <FileText className="h-3.5 w-3.5" strokeWidth={2} />
            {state.fileName}
          </div>
        )}
      </div>

      {(errors.length > 0 || warns.length > 0) && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="validation-issues">
          <div className="text-xs font-medium uppercase tracking-widest text-slate-600 mb-2">
            {t("purchases.upload.issues.title")}
          </div>
          <div className="space-y-1.5">
            {errors.map((i, idx) => (
              <div key={`e-${idx}`} className="flex items-start gap-2 text-sm text-red-700"
                   data-testid={`issue-error-${i.code}`}>
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" strokeWidth={2} />
                <div>
                  <span className="font-medium">{t("purchases.upload.issues.error")}</span>
                  &nbsp;— {i.message}
                </div>
              </div>
            ))}
            {warns.map((i, idx) => (
              <div key={`w-${idx}`} className="flex items-start gap-2 text-sm text-amber-800"
                   data-testid={`issue-warning-${i.code}`}>
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" strokeWidth={2} />
                <div>
                  <span className="font-medium">{t("purchases.upload.issues.warning")}</span>
                  &nbsp;— {i.message}
                </div>
              </div>
            ))}
            {errors.length === 0 && warns.length === 0 && (
              <div className="flex items-center gap-2 text-sm text-emerald-700">
                <CheckCircle2 className="h-4 w-4" strokeWidth={2} />
                Tous les totaux sont cohérents.
              </div>
            )}
          </div>
        </div>
      )}
      {v?.status === "ok" && (errors.length + warns.length === 0) && (
        <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-3"
             data-testid="validation-ok">
          <CheckCircle2 className="h-4 w-4" strokeWidth={2} />
          Tous les totaux sont cohérents.
        </div>
      )}

      {(hasHardDup || hasSoftDup) && (
        <div className={cn(
          "rounded-lg border p-4",
          hasHardDup
            ? "border-red-200 bg-red-50"
            : "border-amber-200 bg-amber-50")}
             data-testid={hasHardDup ? "duplicate-hard-warning" : "duplicate-soft-warning"}>
          <div className={cn("flex items-start gap-2 text-sm",
                             hasHardDup ? "text-red-800" : "text-amber-900")}>
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" strokeWidth={2} />
            <div className="flex-1">
              <div className="font-semibold">
                {hasHardDup
                  ? "Doublon exact détecté"
                  : "Facture similaire déjà enregistrée"}
              </div>
              <div className="text-xs mt-0.5">
                {hasHardDup
                  ? "Une facture avec le même fournisseur et le même numéro existe déjà pour ce tenant. Vérifiez avant d'enregistrer — vous pourrez forcer l'enregistrement si c'est un cas légitime."
                  : "Une facture avec un montant et une date très proches existe déjà. Vérifiez que ce n'est pas la même pièce."}
              </div>
              <div className="mt-2 space-y-1">
                {(hasHardDup ? dups.hard : dups.soft).slice(0, 3).map((d) => (
                  <div key={d.id} className="text-xs bg-white/70 rounded px-2 py-1 border border-white/60 flex items-center justify-between gap-2">
                    <span>
                      <span className="font-mono">{d.invoice_number || "—"}</span>
                      {" · "}
                      <span>{d.supplier_name || "—"}</span>
                      {" · "}
                      <span>{d.invoice_date || "—"}</span>
                    </span>
                    <span className="tabular-nums font-medium">
                      {(Number(d.total_ttc) || 0).toLocaleString("fr-FR",
                        { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-1.5">
            Fournisseur *
          </div>
          <SupplierCombobox
            value={p.supplier_name ? {
              id: p.supplier_id,
              name: p.supplier_name,
              siret: p.supplier_siret,
              siren: p.supplier_siren,
              vat_number: p.supplier_vat_number,
              iban: p.supplier_iban,
            } : null}
            onSelect={(s) => {
              setState((st) => ({ ...st, extracted: { ...st.extracted,
                supplier_id: s.id,
                supplier_name: s.name || "",
                supplier_siret: s.siret || "",
                supplier_siren: s.siren || st.extracted.supplier_siren || "",
                supplier_vat_number: s.vat_number || st.extracted.supplier_vat_number || "",
                supplier_iban: s.iban || st.extracted.supplier_iban || "",
              }}));
            }}
            onClear={() => setState((st) => ({ ...st, extracted: { ...st.extracted,
              supplier_id: null, supplier_name: "", supplier_siret: "",
              supplier_siren: "", supplier_vat_number: "", supplier_iban: "",
            }}))}
          />
          <p className="mt-1 text-[10px] text-slate-400">
            Auto-remplissage SIRET / SIREN / IBAN / TVA depuis votre CRM
            fournisseurs. Évite les doublons manuels à la saisie.
          </p>
        </div>
        <Field label={t("purchases.upload.fields.supplierSiren")}>
          <Input value={p.supplier_siren} onChange={(e) => setField("supplier_siren", e.target.value)}
                 data-testid="field-supplier-siren" />
        </Field>
        <Field label={t("purchases.upload.fields.supplierIban")}>
          <Input value={p.supplier_iban} onChange={(e) => setField("supplier_iban", e.target.value)}
                 data-testid="field-supplier-iban" />
        </Field>
        <Field label={t("purchases.upload.fields.invoiceNumber")} required>
          <Input value={p.invoice_number} onChange={(e) => setField("invoice_number", e.target.value)}
                 data-testid="field-invoice-number" />
        </Field>
        <Field label={t("purchases.upload.fields.invoiceDate")} required>
          <DatePickerFR value={p.invoice_date}
                         onChange={(v) => setField("invoice_date", v)}
                         data-testid="field-invoice-date" />
        </Field>
        <Field label={t("purchases.upload.fields.dueDate")}>
          <DatePickerFR value={p.due_date || ""}
                         onChange={(v) => setField("due_date", v)}
                         data-testid="field-due-date" />
        </Field>
        <Field label={t("purchases.upload.fields.totalHT")}>
          <Input type="number" step="0.01" value={p.total_ht}
                 onChange={(e) => setField("total_ht", e.target.value)}
                 data-testid="field-total-ht" />
        </Field>
        <Field label={t("purchases.upload.fields.totalVAT")}>
          <Input type="number" step="0.01" value={p.total_vat}
                 onChange={(e) => setField("total_vat", e.target.value)}
                 data-testid="field-total-vat" />
        </Field>
        <Field label={t("purchases.upload.fields.totalTTC")} required>
          <Input type="number" step="0.01" value={p.total_ttc}
                 onChange={(e) => setField("total_ttc", e.target.value)}
                 data-testid="field-total-ttc" />
        </Field>
        <Field label={t("purchases.upload.fields.pcgCode")}>
          <Select value={p.pcg_code} onValueChange={(v) => setField("pcg_code", v)}>
            <SelectTrigger data-testid="field-pcg-code">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pcg.map((c) => (
                <SelectItem key={c.code} value={c.code}>
                  <span className="font-mono text-xs mr-2 text-slate-500">{c.code}</span>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </div>

      <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
        <Button variant="ghost" onClick={onCancel} data-testid="review-cancel-btn">
          {t("purchases.upload.cancel")}
        </Button>
        {hasHardDup ? (
          <Button
            variant="destructive"
            onClick={() => { setForceReason(""); setForceDialogOpen(true); }}
            disabled={state.saving}
            data-testid="review-save-force-btn">
            {state.saving ? (
              <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Enregistrement…</>
            ) : (
              <><Save className="h-4 w-4 mr-1.5" /> Enregistrer quand même…</>
            )}
          </Button>
        ) : (
          <Button onClick={() => onSave()} disabled={state.saving}
                  data-testid="review-save-btn">
            {state.saving ? (
              <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Enregistrement…</>
            ) : (
              <><Save className="h-4 w-4 mr-1.5" /> {t("purchases.upload.save")}</>
            )}
          </Button>
        )}
      </div>

      {/* Force override — obligatory justification, chain-signed in audit log */}
      <Dialog open={forceDialogOpen} onOpenChange={setForceDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="force-override-dialog">
          <DialogHeader>
            <DialogTitle>Confirmer l&apos;enregistrement du doublon</DialogTitle>
            <DialogDescription>
              Cette action enregistre une facture identique à une facture déjà existante
              (même fournisseur, même numéro). Un motif est <strong>obligatoire</strong>{" "}
              et sera <strong>signé dans le journal d&apos;audit</strong> — si un double paiement
              est découvert plus tard, ce motif reste tracé.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {(dups.hard || []).slice(0, 3).map((d) => (
              <div key={d.id}
                   className="text-xs bg-red-50 border border-red-200 rounded px-2 py-1.5 flex items-center justify-between gap-2">
                <span>
                  <span className="font-mono">{d.invoice_number || "—"}</span>
                  {" · "}{d.supplier_name || "—"}{" · "}{d.invoice_date || "—"}
                </span>
                <span className="tabular-nums font-medium">
                  {(Number(d.total_ttc) || 0).toLocaleString("fr-FR",
                    { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                </span>
              </div>
            ))}
            <div>
              <Label className="text-xs font-medium">
                Motif de l&apos;enregistrement du doublon <span className="text-red-500">*</span>
              </Label>
              <Textarea value={forceReason}
                         onChange={(e) => setForceReason(e.target.value)}
                         placeholder="Ex : deuxième livraison identique du même fournisseur, ou correction d'un enregistrement précédent erroné (min. 5 caractères)."
                         data-testid="force-reason-input"
                         className="mt-1 min-h-[100px]"
                         maxLength={500} />
              <div className="text-[11px] text-slate-500 mt-1">
                {forceReason.trim().length}/500 caractères — minimum 5.
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setForceDialogOpen(false)}
                    data-testid="force-cancel-btn">
              Annuler
            </Button>
            <Button variant="destructive" onClick={confirmForce}
                    disabled={forceReason.trim().length < 5 || state.saving}
                    data-testid="force-confirm-btn">
              {state.saving
                ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Enregistrement…</>
                : <>Enregistrer le doublon</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-xs font-medium text-slate-600">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
